import React, { useCallback, useMemo } from 'react';
import { type WrappedComponentProps, injectIntl } from 'react-intl-next';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ConfluenceLogo } from '@atlaskit/logo';
import { Anchor, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import OriginTracing from '@atlassiansox/origin-tracing';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { StyledFooter, PoweredBy } from './styled';
import { useThemeContext } from '../theme';
import messages from './messages';

interface OwnProps {
	hasAccessToConfluence: boolean;
}

const buttonStyles = xcss({
	marginInlineStart: 'space.050',
	paddingInline: 'space.100',
	paddingBlock: 'space.050',
	height: 'size.200',
	display: 'inline-flex',
	alignItems: 'center',
	borderRadius: 'border.radius',
	backgroundColor: 'color.background.neutral.subtle',
	color: 'color.text',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const PoweredByConfluenceFooter = ({
	intl,
	hasAccessToConfluence,
}: OwnProps & WrappedComponentProps) => {
	const origin = useMemo(() => new OriginTracing({ product: 'jira' }), []);
	const theme = useThemeContext();
	const handlePoweredByClick = useCallback(
		(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			const originAttributes = origin.toAnalyticsAttributes({
				hasGeneratedId: true,
			});
			fireUIAnalytics(analyticsEvent, 'poweredByConfluenceButton', originAttributes);
		},
		[origin],
	);
	return hasAccessToConfluence ? (
		<StyledFooter>
			<PoweredBy theme={theme}>
				{intl.formatMessage(messages.poweredBy)}
				<Tooltip content={intl.formatMessage(messages.poweredByTooltip)}>
					<Anchor
						onClick={handlePoweredByClick}
						href={origin.addToUrl('/wiki')}
						target="_blank"
						xcss={buttonStyles}
					>
						<ConfluenceLogo size="xsmall" appearance="neutral" />
					</Anchor>
				</Tooltip>
			</PoweredBy>
		</StyledFooter>
	) : null;
};

export default injectIntl(PoweredByConfluenceFooter);
